import { useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { app } from '@microsoft/teams-js';
import { getCallGroupsCurrentUser as apiGetCallGroupsCurrentUser, setAvailabilityCurrentUser as apiSetAvailabilityCurrentUser } from "../apis/cyberGateManagementApi/cyberGateApiClient";
import { CallGroup } from "../apis/cyberGateManagementApi/types";
import { CallGroupComponent, CallGroupRow } from "./CallGroupComponent/CallGroupComponent";
import "./AvailabilitySettingsTab.css";
import { TeamsFxContext } from "./Context";
import { appVersion, tenantWhiteList } from "../constants/version";

export default function AvailabilitySettingsTab() {
  const { themeString, teamsfx, inTeams, theme } = useContext(TeamsFxContext);
  const [callGroups, setCallGroups] = useState<CallGroupRow[]>([]);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [tenantId, setTenantId] = useState<string>('');

  useEffect(() => {
    if (inTeams === true) {

      const fetchUserProfile = async () => {
        if (!teamsfx){
          return;
        }

        let userInfo: any = await teamsfx.getUserInfo();

        setTenantId(userInfo.tenantId)
        setCurrentUserEmail(userInfo.preferredUserName);
      }

      fetchUserProfile()
        .catch((error) => setError(error));
    }
  }, [inTeams]);

  useEffect(() => {

    if(currentUserEmail === ''){
      return;
    }
    
    setLoading(true);
    getCallGroupsUsers()

  }, [currentUserEmail]);

  useEffect(() => {
    if(loading === false || error !== undefined){
      app.notifySuccess();
    }
  }, [loading, error]);

  const getCallGroupsUsers = () => {
    apiGetCallGroupsCurrentUser()
      .then((data) => {
        setCallGroups(transferCallGroupsDataForUI(data));
        setLoading(false);        
        setError(undefined);
      })
      .catch((error) => {
        console.log(error);
        setError(error);
        setLoading(false);        
      });
  }

  const transferCallGroupsDataForUI = (callGroupsData: CallGroup[]): CallGroupRow[] => {
    let callGroupsDataSorted = callGroupsData.sort((group1, group2) => {
      let name1 = group1.name.toLowerCase(),
        name2 = group2.name.toLowerCase();

      if (name1 < name2) {
        return -1;
      }
      if (name1 > name2) {
        return 1;
      }
      return 0;
    });

    callGroupsDataSorted.forEach((group) => {
      group.participants = group.participants.sort((entry1, entry2) => {
        let username1 = entry1.teamsUserName.toLowerCase(),
          username2 = entry2.teamsUserName.toLowerCase();

        if (username1 < username2) {
          return -1;
        }
        if (username1 > username2) {
          return 1;
        }
        return 0;
      });
    });

    return callGroupsData.map(group => ({
      callGroupId: group.callGroupId,
      description: group.description,
      participants: group.participants.map(participant => ({
        participantId: participant.participantId,
        available: participant.available,
        teamsUserName: participant.teamsUserName,
        isCurrentUser: currentUserEmail.toLowerCase() === participant.teamsUserName.toLowerCase(),
      })),
      isCurrentUserSupervisor: group.supervisors.some((supervisor) => supervisor.teamsUserName.toLowerCase() === currentUserEmail.toLowerCase()),
      name: group.name,
      isEdit: false
    }));
  };

  const togglePresentedAvailabilityState = (participantId: string): void => {

    let newGroups = cloneDeep(callGroups);

    // find existing topic
    let updatedGroup = newGroups.filter((group) => { return group.participants.some((entry) => entry.participantId === participantId) })[0];
    let updatedEntry = updatedGroup.participants.filter((entry) => entry.participantId === participantId)[0];
    updatedEntry.available = !updatedEntry.available;

    apiSetAvailabilityCurrentUser(updatedGroup.callGroupId, participantId, updatedEntry.available)
      .then(() => {
        setCallGroups(newGroups);
        setError(undefined);
      })
      .catch((error) => {
        console.log(error);
        setError(error);
      });
  }

  const toogleEditebleMode = (groupId: string, editMode: boolean) : void => {
    setCallGroups(data => data.map(el => ({...el, isEdit: !editMode && el.callGroupId !== groupId})))
  }

  const credential = tenantWhiteList.includes(tenantId)

  return (
    <div className={themeString === "default" ? "" : "dark"}>
      <div className="availabilitysettingstab page">
        <div className="narrow-width page-padding">
          {!loading && callGroups.length > 0 && (
            <>
              Choose whether or not to receive calls from the following groups.
              {callGroups.map(group => (
                <CallGroupComponent key={group.callGroupId} group={group} theme={theme} themeName={themeString}
                getCallGroupsUsers={getCallGroupsUsers}
                toogleEditebleMode={toogleEditebleMode}
                  availabilityStateChanged={(participantId) => {
                    togglePresentedAvailabilityState(participantId);
                  }} />
              ))}
            </>
          )}

          {!loading && !error && callGroups.length === 0 && (
            <>
              The user '{currentUserEmail}' is not part of any multiring group or meeting.
            </>
          )}

          {!loading && error && (
            <div className="error">
              An unexpected error occurred. Please try again later. <br /> Details: {error.toString()}
            </div>
          )}
        </div>
        {credential && <div style={{position: 'absolute', top: '15px', left: '20px', color: 'grey'}}> Ver. {appVersion}</div>}
      </div>
    </div> 
  );
}
